/**
 * TtfeTracker — sessionization + dedupe for the TTFE event spine (PLAT-11223).
 *
 * TTFE (time to first edit) = wall clock from file-select in the Add Story
 * flow to the transcript/timeline accepting the user's first edit.
 * Time-to-canvas is the sibling metric, ending at the renderer handshake.
 * Client-observed timestamps are canonical: they include DatoCMS polling
 * latency (~15s quantization), which is what users actually experience.
 *
 * A session starts at file-select — before the story record exists — keyed by
 * the upload modal id, and is re-keyed to the storyId once the blank story is
 * created. Story-linked sessions persist to sessionStorage so a same-tab
 * reload mid-pipeline keeps the clock. Every post-file-select emitter is a
 * no-op for stories without a live session: an editor opened on an old story
 * is not a TTFE measurement.
 *
 * This module holds state only; the actual PostHog emission goes through
 * EventTracker like every other product analytics event.
 */

import {
  trackTtcCanvasReady,
  trackTtfeFileSelected,
  trackTtfeFirstEditAccepted,
  trackTtfeSidecarDemuxCompleted,
  trackTtfeSidecarSkipped,
  trackTtfeSidecarUploadCompleted,
  trackTtfeSignalObserved,
} from './EventTracker';

export type TtfeSignal =
  | 'combine_results'
  | 'generate_waveform'
  | 'extract_thumbnails_from_low_res'
  | '270p'
  | 'audio_corrected';

interface TtfeSession {
  sessionId: string;
  fileSelectedAtEpochMs: number;
  storyId?: string;
  observedSignals: TtfeSignal[];
  firstEditFired: boolean;
  canvasReadyFired: boolean;
  // PLAT-11697 Audio Sidecar, each at most once per session.
  sidecarDemuxFired?: boolean;
  sidecarUploadFired?: boolean;
  sidecarSkipReason?: string;
}

/**
 * Structural subset of `NewStoryUpdate` this module needs — declared here so
 * analytics never imports a component-layer type.
 */
export interface TtfeSignalCarrier {
  id: string;
  uploadLog?: {
    combineResults?: { status?: string };
    generateWaveform?: { status?: string };
    extractThumbnailsFromLowRes?: { status?: string };
    compressVideo?: {
      output?: { jobs?: Array<{ resolution?: string; status?: string }> };
    };
    /** Written by the corrected-audio pipeline (PLAT-11226); absent today. */
    audioCorrected?: { status?: string } | null;
  } | null;
}

const STORAGE_KEY = 'arbor.ttfe.sessions.v1';

/** Sessions awaiting a story record, keyed by upload-modal id. */
const pendingByModal = new Map<string, TtfeSession>();
/** Story-linked sessions, keyed by storyId. Mirrored to sessionStorage. */
let byStory: Map<string, TtfeSession> | null = null;

function loadStorySessions(): Map<string, TtfeSession> {
  if (byStory) return byStory;
  byStory = new Map();
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed: Record<string, TtfeSession> = JSON.parse(raw);
      for (const [storyId, session] of Object.entries(parsed)) {
        byStory.set(storyId, session);
      }
    }
  } catch {
    // Storage unavailable or corrupt — sessions just don't survive reload.
  }
  return byStory;
}

function persistStorySessions(): void {
  try {
    window.sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(Object.fromEntries(loadStorySessions())),
    );
  } catch {
    // Best-effort only.
  }
}

function elapsedMs(session: TtfeSession): number {
  return Math.max(0, Math.round(Date.now() - session.fileSelectedAtEpochMs));
}

/**
 * Clock start. Fires `ttfe.file_selected` and opens a session keyed by the
 * upload modal until {@link storyCreated} links the storyId.
 */
export function fileSelected(
  modalKey: string,
  props: { fileSizeBytes: number; fileCount: number; fileType?: string },
): void {
  const session: TtfeSession = {
    sessionId: window.crypto.randomUUID(),
    fileSelectedAtEpochMs: Date.now(),
    observedSignals: [],
    firstEditFired: false,
    canvasReadyFired: false,
  };
  pendingByModal.set(modalKey, session);
  trackTtfeFileSelected({
    ttfe_session_id: session.sessionId,
    file_size_bytes: props.fileSizeBytes,
    file_count: props.fileCount,
    file_type: props.fileType,
  });
}

/** Links the blank story record to the session opened at file-select. */
export function storyCreated(modalKey: string, storyId: string): void {
  const session = pendingByModal.get(modalKey);
  if (!session) return;
  pendingByModal.delete(modalKey);
  session.storyId = storyId;
  loadStorySessions().set(storyId, session);
  persistStorySessions();
}

/** Story-linked sessions win; a modal-keyed one exists until the blank story does. */
function findSession(storyIdOrModalKey: string): TtfeSession | undefined {
  return (
    loadStorySessions().get(storyIdOrModalKey) ??
    pendingByModal.get(storyIdOrModalKey)
  );
}

function persistIfStoryLinked(session: TtfeSession): void {
  if (session.storyId) persistStorySessions();
}

/**
 * Whether an upload session exists for this key. Lets work that can finish
 * before `fileSelected` (the Audio Sidecar demux starts at file-select, ahead
 * of validation) hold its events until the clock has started.
 */
export function hasSession(storyIdOrModalKey: string): boolean {
  return !!findSession(storyIdOrModalKey);
}

/**
 * Session props to merge into the existing `story_upload_completed` event —
 * the spec adds props to that event rather than introducing a new one.
 */
export function uploadCompletedProps(storyIdOrModalKey: string): {
  ttfe_session_id?: string;
  ttfe_elapsed_ms?: number;
} {
  const session =
    loadStorySessions().get(storyIdOrModalKey) ??
    pendingByModal.get(storyIdOrModalKey);
  if (!session) return {};
  return {
    ttfe_session_id: session.sessionId,
    ttfe_elapsed_ms: elapsedMs(session),
  };
}

// ─── Audio Sidecar (PLAT-11697) ──────────────────────────────────
//
// The demux may finish before the blank story exists, so these accept either
// key. Like every other emitter here they are no-ops without a session.

/** The on-device demux finished (a gate fail still reports, with 0 bytes). */
export function sidecarDemuxCompleted(
  storyIdOrModalKey: string,
  props: {
    demuxMs: number;
    originalBytes: number;
    sidecarBytes: number;
    gatePass: boolean;
  },
): void {
  const session = findSession(storyIdOrModalKey);
  if (!session || session.sidecarDemuxFired) return;
  session.sidecarDemuxFired = true;
  persistIfStoryLinked(session);
  trackTtfeSidecarDemuxCompleted({
    story_id: session.storyId,
    ttfe_session_id: session.sessionId,
    ttfe_elapsed_ms: elapsedMs(session),
    demux_ms: props.demuxMs,
    original_bytes: props.originalBytes,
    sidecar_bytes: props.sidecarBytes,
    gate_pass: props.gatePass,
  });
}

/** The Sidecar landed in S3 (before the Original). */
export function sidecarUploadCompleted(
  storyIdOrModalKey: string,
  props: { wallMs: number; bytes: number },
): void {
  const session = findSession(storyIdOrModalKey);
  if (!session || session.sidecarUploadFired) return;
  session.sidecarUploadFired = true;
  persistIfStoryLinked(session);
  trackTtfeSidecarUploadCompleted({
    story_id: session.storyId,
    ttfe_session_id: session.sessionId,
    ttfe_elapsed_ms: elapsedMs(session),
    wall_ms: props.wallMs,
    bytes: props.bytes,
  });
}

/** The upload took the Server-Extract Path. First reason per session wins. */
export function sidecarSkipped(
  storyIdOrModalKey: string,
  skipReason: string,
): void {
  const session = findSession(storyIdOrModalKey);
  if (!session || session.sidecarSkipReason) return;
  session.sidecarSkipReason = skipReason;
  persistIfStoryLinked(session);
  trackTtfeSidecarSkipped({
    story_id: session.storyId,
    ttfe_session_id: session.sessionId,
    ttfe_elapsed_ms: elapsedMs(session),
    skip_reason: skipReason,
  });
}

/** Readiness predicates per signal, matching `Dashboard/utils.ts` semantics. */
const SIGNAL_PREDICATES: Array<{
  signal: TtfeSignal;
  isReady: (uploadLog: NonNullable<TtfeSignalCarrier['uploadLog']>) => boolean;
}> = [
  {
    signal: 'combine_results',
    isReady: log => log.combineResults?.status === 'success',
  },
  {
    signal: 'generate_waveform',
    isReady: log => log.generateWaveform?.status === 'success',
  },
  {
    signal: 'extract_thumbnails_from_low_res',
    isReady: log => log.extractThumbnailsFromLowRes?.status === 'success',
  },
  {
    signal: '270p',
    isReady: log =>
      !!log.compressVideo?.output?.jobs?.some(
        j =>
          j.resolution === '270' &&
          (j.status || '').toLowerCase() === 'complete',
      ),
  },
  {
    signal: 'audio_corrected',
    isReady: log => log.audioCorrected?.status === 'success',
  },
];

/**
 * Fires `ttfe.signal_observed` for each readiness signal newly visible on a
 * story update from the DatoCMS query subscription. Once per signal per
 * session; no-op for stories without a session.
 */
export function observeStorySignals(story: TtfeSignalCarrier): void {
  const session = loadStorySessions().get(story.id);
  if (!session || !story.uploadLog) return;

  let changed = false;
  for (const { signal, isReady } of SIGNAL_PREDICATES) {
    if (session.observedSignals.includes(signal)) continue;
    if (!isReady(story.uploadLog)) continue;
    session.observedSignals.push(signal);
    changed = true;
    trackTtfeSignalObserved({
      story_id: story.id,
      signal,
      ttfe_session_id: session.sessionId,
      ttfe_elapsed_ms: elapsedMs(session),
    });
  }
  if (changed) persistStorySessions();
}

/**
 * Clock stop. Fires `ttfe.first_edit_accepted` once per session, on the first
 * executed editor command.
 */
export function firstEditAccepted(
  storyId: string,
  editSurface: 'transcript' | 'timeline',
): void {
  const session = loadStorySessions().get(storyId);
  if (!session || session.firstEditFired) return;
  session.firstEditFired = true;
  persistStorySessions();
  trackTtfeFirstEditAccepted({
    story_id: storyId,
    edit_surface: editSurface,
    ttfe_session_id: session.sessionId,
    ttfe_elapsed_ms: elapsedMs(session),
  });
}

/**
 * Second metric. Fires `ttc.canvas_ready` once per session, when the renderer
 * handshake completes.
 */
export function canvasReady(storyId: string, source: '270p'): void {
  const session = loadStorySessions().get(storyId);
  if (!session || session.canvasReadyFired) return;
  session.canvasReadyFired = true;
  persistStorySessions();
  trackTtcCanvasReady({
    story_id: storyId,
    source,
    ttfe_session_id: session.sessionId,
    ttc_elapsed_ms: elapsedMs(session),
  });
}
